import { createInstance, i18n } from 'i18next';
import HttpApi from 'i18next-http-backend';

import { Locale, ShopBundle } from '../types';

import { captureException } from './sentryService';

export const supportedLngs: Locale[] = [
  'bg-BG',
  'cs-CZ',
  'da-DK',
  'de-AT',
  'de-CH',
  'de-DE',
  'el-GR',
  'en',
  'en-GB',
  'en-IE',
  'es-ES',
  'fi-FI',
  'fr-BE',
  'fr-CH',
  'fr-FR',
  'hr-HR',
  'hu-HU',
  'it-IT',
  'nl-NL',
  'nb-NO',
  'pl-PL',
  'pt-PT',
  'ro-RO',
  'ru-RU',
  'sk-SK',
  'sl-SI',
  'sv-SE',
  'zu',
];

export function isLocale(value: unknown): value is Locale {
  return typeof value === 'string' ? (supportedLngs as string[]).indexOf(value) !== -1 : false;
}

let instance: i18n | undefined;
let initializing = false;

export const initializeI18n = async (shopBundle: ShopBundle, locale: Locale) => {
  if (instance) {
    return instance;
  }
  if (initializing) {
    return await new Promise<i18n>((resolve) => waitForInstance(resolve));
  }

  initializing = true;
  const loadingInstance = createInstance();
  await loadingInstance.use(HttpApi).init({
    compatibilityJSON: 'v3', //This is needed for Lokalise compatibility, they will work on provide compatibility for v4
    resources: {},
    partialBundledLanguages: true,
    backend: {
      loadPath: (lng: string[]) => {
        const lang = lng[0].replace('-', '_');
        return `/media/zpp-assets/payment-frontend/locales/${shopBundle}/${lang}.json`;
      },
      reloadInterval: 30 * 60 * 1000, //reload every 30 mins
    },
    react: {
      useSuspense: false,
    },
    interpolation: { prefix: '{', suffix: '}', escapeValue: false },
    lng: locale,
    load: 'currentOnly',
    fallbackLng: 'zu',
    supportedLngs,
    debug: false, // set this to true to see your requests being logged
  });

  instance = loadingInstance;
  initializing = false;

  instance.on('failedLoading', (lng: string, ns: string, message: string) => {
    captureException(new Error(`Could not load ${lng} in ns ${ns}, error message: ${message}`));
  });

  return instance;
};

const waitForInstance = (resolve: (value: i18n) => void) => {
  if (instance) {
    resolve(instance);
  } else {
    setTimeout(() => waitForInstance(resolve), 100);
  }
};
